import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { LogIn, LogOut, Clock, MapPin, CheckCircle, AlertTriangle } from "lucide-react";
import { formatDateTime } from "../../lib/dateUtils";

export const JobDetailCheckInStatus = ({ job }) => {
  if (!job.check_in && !job.check_out) return null;

  return (
    <Card className="border-slate-200 shadow-sm rounded-sm" data-testid="checkin-status-card">
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Check In */}
          {job.check_in && (
            <div className="flex items-start gap-3">
              <div className="p-2 bg-emerald-50 rounded-sm">
                <LogIn className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Checked In</p>
                <p className="font-medium text-slate-800">
                  {formatDateTime(job.check_in.timestamp)}
                </p>
                <p className="text-xs text-slate-500">by {job.check_in.user_name}</p>
              </div>
            </div>
          )}

          {/* Check Out */}
          {job.check_out && (
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-50 rounded-sm">
                <LogOut className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Checked Out</p>
                <p className="font-medium text-slate-800">
                  {formatDateTime(job.check_out.timestamp)}
                </p>
                <p className="text-xs text-slate-500">by {job.check_out.user_name}</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const JobDetailCheckInButtons = ({
  job,
  isMechanic,
  gettingLocation,
  onCheckIn,
  onCheckOut,
  hasRequiredPhotos,
  hasSerialNumber,
  hasSignature,
}) => {
  // Only show for mechanics
  if (!isMechanic) return null;

  // Job must be assigned to this mechanic
  const canCheckIn = job.status === "pending" && !job.check_in;
  const canCheckOut = job.status === "in_progress" && job.check_in && !job.check_out;

  if (!canCheckIn && !canCheckOut) return null;

  return (
    <div className="flex gap-2" data-testid="checkin-buttons">
      {canCheckIn && (
        <Button
          onClick={onCheckIn}
          disabled={gettingLocation}
          className="bg-emerald-600 hover:bg-emerald-700 rounded-sm"
          data-testid="check-in-btn"
        >
          <LogIn className="w-4 h-4 mr-2" />
          {gettingLocation ? "Getting Location..." : "Check In"}
        </Button>
      )}

      {canCheckOut && (
        <div className="flex flex-col gap-2">
          <Button
            onClick={onCheckOut}
            disabled={gettingLocation}
            className="bg-blue-600 hover:bg-blue-700 rounded-sm"
            data-testid="check-out-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {gettingLocation ? "Getting Location..." : "Check Out"}
          </Button>
          
          {/* Requirements status */}
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge
              variant="outline"
              className={hasSerialNumber ? "text-emerald-600 border-emerald-300" : "text-amber-600 border-amber-300"}
            >
              {hasSerialNumber ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
              Serial Number
            </Badge>
            <Badge
              variant="outline"
              className={hasRequiredPhotos ? "text-emerald-600 border-emerald-300" : "text-amber-600 border-amber-300"}
            >
              {hasRequiredPhotos ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
              Work Photos
            </Badge>
            <Badge
              variant="outline"
              className={hasSignature ? "text-emerald-600 border-emerald-300" : "text-amber-600 border-amber-300"}
            >
              {hasSignature ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
              Signature
            </Badge>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetailCheckInStatus;
